import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { getFullImageUrl } from 'client/utils/image-helpers';
import { IMAGE_SIZES } from 'site-modules/shared/constants/editorial/editorial';
import { EmbeddedYoutubeAsync } from 'site-modules/shared/components/video/embedded-youtube/embedded-youtube-async';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { RoutedCarouselWithCallback } from 'site-modules/shared/components/editorial/photoflipper-video-image-top-rated/carousel-with-callback';

import { RenderMeatballs } from './render-meatballs';

import './photoflipper-video-image-top-rated.scss';

export const videoToggleCallback = () => {
  const video = document.querySelector('.youtube-video');
  if (video && video.contentWindow && video.src) {
    video.contentWindow.postMessage('{"event":"command","func":"stopVideo","args":""}', new URL(video.src).origin);
  }
};

export const clickTargetButton = (index, ref) => {
  const targetDotButton = ref.current.querySelector(`.slick-dots li:nth-child(${index + 1}) button`);
  if (targetDotButton) targetDotButton.click();
};

export function PhotoflipperVideoImageTopRated({
  vehicleGallery = null,
  fullImageSize = 'lg',
  aspectRatio = '16:9',
  wrapperClass = 'mb-1',
  playerComponent = EmbeddedYoutubeAsync,
  isCenterMode = false,
  centerPadding = null,
  showMeatballs = true,
  slickSlideClassName = null,
  isPreHovered = false,
  thumbnailPhotoClass = null,
}) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const ref = useRef();

  const onBeforeChange = useCallback(
    (from, to) => {
      setCurrentSlideIndex(to);
    },
    [setCurrentSlideIndex]
  );

  const handleClickEvent = useCallback(
    event => {
      const index = parseInt(event.target.getAttribute('data-index'), 10);

      clickTargetButton(index, ref);
      setCurrentSlideIndex(index);
    },
    [setCurrentSlideIndex]
  );

  if (!get(vehicleGallery, 'length')) {
    return null;
  }

  const vehicleGalleryToUse = vehicleGallery.filter(slide => slide.href || slide.videoId);
  const carouselSlides = vehicleGalleryToUse.map((slide, index) => {
    const { videoId, videoTitle, href, title } = slide;
    const imageUri = getFullImageUrl(href, IMAGE_SIZES[fullImageSize]);
    const PlayerComponent = playerComponent;

    if (videoId) {
      return (
        <div key={`${href}-${title}`} className="slide youtube-video-container" data-index={index}>
          <PlayerComponent
            videoId={videoId}
            videoTitle={videoTitle}
            creativeId={`video-${videoId}`}
            autoplay={false}
            className="youtube-video"
            autoplayOnChange
            loaderClassName="w-100 h-100 pos-a top-0 p-0 border-0 no-focus"
          />
        </div>
      );
    }

    return (
      <div key={`${href}-${title}`} className="slide" data-index={index}>
        <img className="photo my-auto" alt={title || ''} src={imageUri} />
      </div>
    );
  });

  const sortedArray = vehicleGallery.reduce((acc, cur) => {
    if (cur.videoId) {
      acc.unshift(cur);
    } else {
      acc.push(cur);
    }
    return acc;
  }, []);

  const photoOnly = !!(sortedArray[0] && !sortedArray[0].videoId);
  const showVideoPreviewOnly = sortedArray[0].videoThumbOnly;
  const isBestOfBest = !showVideoPreviewOnly && !photoOnly;

  return (
    <div ref={ref}>
      <AspectRatioContainer
        aspectRatio={showVideoPreviewOnly ? aspectRatio : '913:514'}
        className={classnames(
          'pos-r photoflipper-video-image-container rounded photoflipper-image-container',
          wrapperClass,
          {
            'is-pre-hovered': isPreHovered,
          }
        )}
      >
        <Container
          fluid
          className="hero-carousel editorial-hero-carousel px-0"
          data-tracking-parent="edm-entry-editorial-hero-carousel"
        >
          <Row className="g-0">
            <Col>
              <RoutedCarouselWithCallback
                autoplay={false}
                lazyLoad
                currentSlideIndex={currentSlideIndex}
                callback={!photoOnly ? videoToggleCallback : null}
                dots={showMeatballs}
                beforeChange={onBeforeChange}
                centerMode={isCenterMode}
                centerPadding={centerPadding}
                className={isCenterMode ? classnames('is-center-mode', slickSlideClassName) : slickSlideClassName}
              >
                {carouselSlides}
              </RoutedCarouselWithCallback>
            </Col>
          </Row>
        </Container>
      </AspectRatioContainer>
      {(photoOnly || showVideoPreviewOnly) && showMeatballs && (
        <RenderMeatballs
          currentSlideIndex={currentSlideIndex}
          sortedArray={sortedArray}
          classes={photoOnly ? ' pb-mb-1' : 'pb-1'}
          onClick={handleClickEvent}
        />
      )}
      {isBestOfBest && (
        <div className="pos-r photoflipper-video-image-container mb-0_25 rounded photoflipper-image-container">
          <section className="thumbnails-container mx-auto pb-md-1 hidden-md-down">
            {sortedArray.slice(1, 6).map((slide, index) => (
              <button
                className={classnames('thumbnail-photo pos-r d-block p-0', thumbnailPhotoClass, {
                  'hidden-md-down': index + 1 > 0,
                })}
                key={`${slide.href}`}
                onClick={handleClickEvent}
                data-index={index + 1}
                data-testid={`button-${index + 1}`}
              >
                <img
                  src={getFullImageUrl(slide.href, 700)}
                  alt={slide.title}
                  className="w-100 h-100"
                  loading="lazy"
                  data-index={index + 1}
                />
                <span className="text-white fw-bold text-center hidden-md-up" data-index={index + 1}>
                  View Gallery
                </span>
              </button>
            ))}
          </section>
          {showMeatballs && (
            <RenderMeatballs
              currentSlideIndex={currentSlideIndex}
              sortedArray={sortedArray}
              classes="pb-1 hidden-md-up"
              onClick={handleClickEvent}
            />
          )}
        </div>
      )}
    </div>
  );
}

PhotoflipperVideoImageTopRated.propTypes = {
  vehicleGallery: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string,
      title: PropTypes.string,
      videoId: PropTypes.string,
      videoTitle: PropTypes.string,
    })
  ),
  fullImageSize: PropTypes.string,
  playerComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  aspectRatio: PropTypes.string,
  wrapperClass: PropTypes.string,
  isCenterMode: PropTypes.bool,
  centerPadding: PropTypes.string,
  showMeatballs: PropTypes.bool,
  slickSlideClassName: PropTypes.string,
  isPreHovered: PropTypes.bool,
  thumbnailPhotoClass: PropTypes.string,
};
